import React, { useState } from 'react';
import classnames from 'classnames';
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import type { CustomerState } from '../../../customer-states/types/customer-states.types';

interface ContactFilterDropdownProps {
    filterStateId?: string | undefined;
    setFilterStateId?: (stateId: string | undefined) => void;
    allStates: CustomerState[];
}

const ContactFilterDropdown: React.FC<ContactFilterDropdownProps> = ({
                                                                         filterStateId,
                                                                         setFilterStateId,
                                                                         allStates
                                                                     }) => {
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    // 1. Estado para almacenar el texto de búsqueda
    const [searchTerm, setSearchTerm] = useState('');

    // Helper function to get color based on state type
    const getStateColor = (type: string): string => {
        switch (type) {
            case 'INITIAL': return '#00a884';
            case 'FINAL': return '#ef5350';
            case 'TRANSIENT': return '#ff9800';
            default: return '#667781';
        }
    };

    // Get active state object and color
    const activeStateObj = allStates.find(state => state.id === filterStateId);
    const currentActiveColor = activeStateObj ? getStateColor(activeStateObj.type) : '#667781';

    const getBgColor = () => {
        if (!filterStateId || !activeStateObj) return '';
        return `${getStateColor(activeStateObj.type)}15`;
    };

    const getHoverBgColor = () => {
        if (!filterStateId || !activeStateObj) return '#f0f2f5';
        return `${getStateColor(activeStateObj.type)}25`;
    };

    // 2. Función que maneja el cambio en el input de búsqueda
    const handleSearchStatus = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    };

    // 3. Filtrar los estados según el término de búsqueda
    const filteredStates = allStates.filter(state =>
        state.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Limpiar el buscador cuando se cierra o abre el dropdown
    const handleToggle = () => {
        setIsFilterOpen(!isFilterOpen);
        setSearchTerm(''); // Resetea la búsqueda al cerrar/abrir
    };

    return (
        <Dropdown isOpen={isFilterOpen} toggle={handleToggle}>
            <DropdownToggle
                tag="button"
                type="button"
                className={classnames(
                    'btn',
                    'rounded-circle',
                    'd-flex',
                    'align-items-center',
                    'justify-content-center',
                    'flex-shrink-0',
                    'position-relative'
                )}
                style={{
                    width: '35px',
                    height: '35px',
                    transition: 'all 0.2s ease',
                    borderStyle: 'solid',
                    borderWidth: '1px',
                    borderColor: filterStateId ? currentActiveColor : '#e9edef',
                    color: currentActiveColor,
                    backgroundColor: getBgColor(),
                    boxShadow: isFilterOpen && filterStateId ? `0 0 0 0.15rem ${currentActiveColor}33` : 'none'
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = getHoverBgColor();
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = getBgColor();
                }}
            >
                <i className="ri-filter-3-line fs-5"></i>

                {filterStateId && (
                    <span
                        className="position-absolute top-0 end-0"
                        style={{
                            width: '8px',
                            height: '8px',
                            backgroundColor: currentActiveColor,
                            borderRadius: '50%',
                            border: '2px solid white',
                            marginTop: '-2px',
                            marginRight: '-2px'
                        }}
                    ></span>
                )}
            </DropdownToggle>
            <DropdownMenu end className="shadow" style={{ minWidth: '220px' }}>
                <DropdownItem header>Filtrar por Estado</DropdownItem>

                {/* 4. Input de búsqueda estilizado */}
                <DropdownItem header className="pt-0 pb-2">
                    <input
                        type="text"
                        className="form-control form-control-sm border-secondary-subtle shadow-none"
                        placeholder="Buscar estado..."
                        value={searchTerm}
                        onChange={handleSearchStatus}
                        onClick={(e) => e.stopPropagation()} // Evita que el dropdown se cierre al hacer clic en el input
                    />
                </DropdownItem>

                <DropdownItem divider className="mt-0" />
                <DropdownItem
                    disabled={!filterStateId}
                    onClick={() => {
                        if (setFilterStateId) setFilterStateId(undefined);
                        setIsFilterOpen(false);
                    }}
                >
                    <i className="ri-close-line me-2"></i>
                    Limpiar filtro
                </DropdownItem>
                <DropdownItem divider />

                {/* 5. Renderizar los estados filtrados en lugar de 'allStates' */}
                {filteredStates.length > 0 ? (
                    filteredStates.map((state) => (
                        <DropdownItem
                            key={state.id}
                            active={filterStateId === state.id}
                            onClick={() => {
                                if (setFilterStateId) setFilterStateId(state.id);
                                setIsFilterOpen(false);
                            }}
                        >
                            <div className="d-flex align-items-center">
                                <div
                                    className="rounded-circle me-2"
                                    style={{
                                        width: '12px',
                                        height: '12px',
                                        backgroundColor: getStateColor(state.type)
                                    }}
                                ></div>
                                {state.name}
                                {filterStateId === state.id && (
                                    <i className="ri-check-line ms-auto text-success"></i>
                                )}
                            </div>
                        </DropdownItem>
                    ))
                ) : (
                    /* Mensaje si la búsqueda no arroja resultados */
                    <div className="text-muted text-center py-2 small">
                        No se encontraron estados
                    </div>
                )}
            </DropdownMenu>
        </Dropdown>
    );
};

export default ContactFilterDropdown;
